import { FC, useEffect, useState } from "react";
import { DefaultGrid } from "../../../components/grids/default/defaultGrid";
import { PluginWrapper, ToolbarWrapper } from "../../../components/plugins";
import { Spinner } from "../../../components/spiner/Spinner";
import useGridFilter, { CreateSelectorGridFilter } from "../../../system/hooks/useGridFilter";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { NewsChatDataProvider } from "../../../Services/DataProviders/NewsChatDataProvider";
import { INews2User, INewsMakerItemCreateDTO, INewsMakerItemEditDTO, INewsMakerViewDTO } from "../../../libs/coreapi-dto/service/newsChat";
import { NewsMakerPanel } from "./components/NewsMakerPanel";
import NewsMakerCreatorModal from "./NewsMakerCreatorModal";
import SearchModalWindowAntd from "../../../components/modalWindows/SearchModalWindowAntd/SearchModalWindowAntd";
import NewsToUserModal from "./components/NewsToUserModal";
import { News2UserDataProvider } from "../../../Services/DataProviders/News2UserDataProvider";
import styles from './styles/NewsMaker.module.scss'
import { DateTime } from "luxon";

export const NewsMakerGrid: FC<IGridProps> = (props) => {
    const appContext = useAppContext()
    const newsChatDataProvider = new NewsChatDataProvider(appContext.coreApiService);
    const news2UserDataProvider = new News2UserDataProvider(appContext.coreApiService);

    const [data, setData] = useState<INewsMakerViewDTO[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [viewState, setViewState] = useState<GridStateType>("view");
    const [gridFilter, dispatchGridFilter] = useGridFilter(CreateSelectorGridFilter);
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [selectedItems, setSelectedItems] = useState<IGridRow[]>(props.data ?? []);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);
    const [modalWindow, setModalWindow] = useState(<></>)
    const [isSearchModalVisible, setIsSearchModalVisible] = useState(false);
    const [isUserModalVisible, setIsUserModalVisible] = useState(false);

    const loadData = () => {
        setIsSubmitting(true)

        try {
            newsChatDataProvider.getView(gridFilter, (data, totalCount) => {
                setData(data);
                setTotalCount(totalCount);
                setIsSubmitting(false)
            });
        } catch (error) {
            setIsSubmitting(false)
            console.error(error);
        }
    };

    useEffect(() => {
        loadData();
    }, [gridFilter]);


    useEffect(() => {
        switch (viewState) {
            case 'refresh':
                setIsSubmitting(true)
                newsChatDataProvider.getView(gridFilter, async (data, totalCount) => {
                    setData(data)
                    setViewState('view')
                    setTotalCount(totalCount)
                    setIsSubmitting(false)
                    setSelectedItem(undefined)
                })
                break;
            case 'edit':
                renderEditor();
                break;
            case 'create':
                renderCreator();
                break;
            default:
                setModalWindow(<></>)
        }
    }, [viewState])

    function renderEditor() {
        newsChatDataProvider.getById(selectedItem?.idGlobal as string, (entity) => {
            setModalWindow(
                <NewsMakerCreatorModal
                    variant='edit'
                    data={entity}
                    save={(model: INewsMakerItemEditDTO | INewsMakerItemCreateDTO) => {
                        newsChatDataProvider?.update(selectedItem?.idGlobal as string, model as INewsMakerItemEditDTO, () => {
                            setViewState("refresh");
                            setModalWindow(<></>);
                        })
                    }}
                    cancel={() => {
                        setViewState("view");
                        setModalWindow(<></>);
                    }}
                />
            )
        })
    }

    function renderCreator(): void {
        const newNews: INewsMakerItemCreateDTO = {
            id: 0,
            subject: '',
            description: '',
            startDate: DateTime.now(),
            dueDate: DateTime.now(),
            estimatedHour: 0,
            priority: 1
        };
        setModalWindow(
            <NewsMakerCreatorModal
                data={newNews}
                variant='create'
                save={(data: INewsMakerItemCreateDTO) => {
                    newsChatDataProvider.create(data, (id: string) => {
                        setViewState("refresh");
                        setModalWindow(<></>);
                    })
                }}
                cancel={() => {
                    setViewState("view");
                    setModalWindow(<></>);
                }}
            />
        )
    }

    const handleSearchToggle = () => {
        setIsSearchModalVisible(!isSearchModalVisible);
        setViewState(isSearchModalVisible ? "return" : "search");
    };

    const hasActiveFilters = () => {
        if (!gridFilter?.columnFilters) return false;

        return gridFilter.columnFilters.some(filter =>
            filter.name !== "deleted"
        );
    };

    return (
        <PluginWrapper>
            {
                <ToolbarWrapper className={styles.toolbarWrapper}>
                    <NewsMakerPanel
                        add={{
                            onClick: () => {
                                setViewState('create')
                            }
                        }}
                        refresh={{
                            onClick: () => {
                                setViewState('refresh')
                            }
                        }}
                        edit={{
                            onClick: () => setViewState('edit'),
                            disabled: selectedItem === undefined || selectedItem?.isDeleted
                        }}
                        delete={{
                            onClick: () => {
                                if (selectedItem) {
                                    newsChatDataProvider.deleteById(selectedItem.idGlobal, () => {
                                        setViewState("refresh");
                                    });
                                }
                            },
                            disabled: !selectedItem || selectedItem?.isDeleted === true
                        }}
                        restore={{
                            onClick: () => {
                                if (selectedItem)
                                    newsChatDataProvider.restore(selectedItem.idGlobal, () => {
                                        setViewState('refresh')
                                    });
                            },
                            disabled: !selectedItem || !selectedItem.isDeleted,
                        }}
                        search={{
                            onClick: handleSearchToggle,
                            disabled: false,
                        }}
                        assignToAll={{
                            onClick: () => {
                                if (selectedItem) {
                                    newsChatDataProvider.assignToAll(selectedItem.idGlobal, () => {
                                        setViewState("refresh");
                                    });
                                }
                            },
                            disabled: selectedItem === undefined || selectedItem?.isDeleted
                        }}
                        assignToUser={{
                            onClick: () => {
                                if (selectedItem) {
                                    setIsUserModalVisible(true)
                                }
                            },
                            disabled: !selectedItem || selectedItem?.isDeleted === true
                        }}
                        hasActiveFilters={hasActiveFilters()}
                        permission={props.plugin.permission}
                    />
                    {/*Чекбокс можно сделать если будет поле deleted*/}
                    {/* <CheckBox
                        label='Удаленные'
                        defaultChecked={deleted}
                        onChanged={(checked) => {
                            dispatchGridFilter({ type: "showDeleted", payload: checked })
                            setViewState("filter")
                            setDeleted(checked)
                        }}
                        id={uuidv4()}
                        disabled={false}
                    /> */}
                </ToolbarWrapper>
            }
            {
                (isSubmitting) ? <Spinner />
                    :
                    <DefaultGrid
                        gridId={props.gridId}
                        data={data}
                        separator
                        singleDirectory
                        totalCount={totalCount}
                        filter={gridFilter}
                        dataProvider={newsChatDataProvider}
                        plugin={props.plugin}
                        selectedItem={selectedItem}
                        selectedItems={selectedItems}
                        multipleSelect={props.multipleSelect}
                        onDoubleClick={(row) => props.onDoubleClick?.(row)}
                        onSelect={(row) => {
                            setSelectedItem(row)
                            props.onSelect?.(row)
                        }}
                        onMultipleSelect={(rows) => {
                            setSelectedItems(rows)
                            props?.onMultipleSelect?.(rows)
                        }}
                        onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                        onFilterDelete={(i) => dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                        onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                        onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                        onEnter={() => setViewState("edit")}
                    />
            }
            {modalWindow}
            {isSearchModalVisible && (
                <SearchModalWindowAntd
                    columns={props.plugin?.columns.filter((x) => x.visibility === true) ?? []}
                    defaultFilters={gridFilter?.columnFilters}
                    search={(columnFilters) => {
                        dispatchGridFilter({ type: 'search', payload: columnFilters });
                        setViewState('refresh');
                    }}
                    cancel={handleSearchToggle}
                    open={true}
                    onCancel={handleSearchToggle}
                />
            )}
            {isUserModalVisible && (
                <NewsToUserModal
                    save={(idUserGlobal) => {
                        if (selectedItem && idUserGlobal) {
                            const newsToUser: INews2User = {
                                redmineNewsId: selectedItem.idGlobal ?? '',
                                userId: idUserGlobal,
                                isViewed: false
                            };
                            news2UserDataProvider.postNews2User(newsToUser, () => {
                                setViewState("refresh");
                                setIsUserModalVisible(false);
                            });
                        }
                    }}
                    cancel={() => setIsUserModalVisible(false)}
                />
            )}
        </PluginWrapper>
    )
}